import { useSyncExternalStore } from "react";

// Real-time patient signals for a telehealth visit, derived from the appointment the schedule already polls
// (GET /api/edi/televisit/appointments → intake_answers, patient_questions, patient_documents, messages).
// The first time a visit is seen in this browser session its current items become the baseline; anything
// that arrives after that is "new" until someone clicks Got it or Jump to. UI state only (sessionStorage).
const KEY = "revq.th.signals";
let store = (() => { try { return JSON.parse(sessionStorage.getItem(KEY) || "{}"); } catch { return {}; } })();
let ver = 0;
const subs = new Set();
const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify(store)); } catch { /* full */ } ver++; subs.forEach((f) => f()); };

export function itemsOf(a) {
  const out = [];
  (a.intake_answers || []).forEach((x) => {
    const q = x.question || x.id || "Question", ans = String(x.answer ?? x.value ?? "").trim();
    if (ans) out.push({ key: `ans:${q}|${ans}`, kind: "answer", at: a.intake_completed_at || a.updated_at, title: "Intake answer", q, body: ans });
  });
  (a.patient_questions || []).forEach((x) => out.push({ key: `q:${x.id || x.text}`, kind: "question", at: x.at, title: "Question from the patient", body: x.text || x.question || "" }));
  (a.patient_documents || []).forEach((x) => out.push({ key: `doc:${x.doc_id}`, kind: "document", at: x.uploaded_at, title: "Document uploaded", body: x.filename, docId: x.doc_id }));
  (a.messages || []).filter((m) => m.sender === "patient").forEach((m) => out.push({ key: `msg:${m.at}`, kind: "message", at: m.at, title: "Message", body: m.text }));
  return out;
}

// Called with every fresh poll: sets the baseline once per visit per session.
export function observe(rows) {
  let changed = false;
  rows.forEach((a) => {
    const id = a.appointment_id;
    if (!store[id]) { store[id] = { seen: itemsOf(a).map((x) => x.key) }; changed = true; }
  });
  if (changed) save();
}

export const newItems = (a) => {
  const s = store[a.appointment_id];
  if (!s) return [];
  const seen = new Set(s.seen);
  return itemsOf(a).filter((x) => !seen.has(x.key)).sort((x, y) => String(y.at || "").localeCompare(String(x.at || "")));
};

export function ack(id, keys) {
  const s = store[id] || (store[id] = { seen: [] });
  s.seen = [...new Set([...s.seen, ...[].concat(keys)])];
  save();
}

export function useSignalsVersion() {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => ver);
}

const KIND = { answer: "answer", question: "question", document: "document", message: "message" };
export function newSummary(items) {
  const c = {};
  items.forEach((x) => { c[x.kind] = (c[x.kind] || 0) + 1; });
  return Object.entries(c).map(([k, n]) => `${n} ${KIND[k]}${n === 1 ? "" : "s"}`).join(" · ");
}
